import { useEffect, useState, useCallback } from "react";
import { Check, X, Loader2, Users, Mail, MessageCircle, UserMinus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";

export default function Friends() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("incoming"); // incoming | outgoing | friends
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/friends/requests/incoming"),
        api.get("/friends/requests/outgoing"),
        api.get("/friends"),
      ]);
      setIncoming(a.data);
      setOutgoing(b.data);
      setFriends(c.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const accept = async (id) => {
    try {
      await api.post(`/friends/requests/${id}/accept`);
      toast.success("Freundschaft bestätigt 🤝");
      loadAll();
    } catch { toast.error("Fehler"); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/friends/requests/${id}/reject`);
      toast.success("Anfrage abgelehnt");
      loadAll();
    } catch { toast.error("Fehler"); }
  };
  const unfriend = async (uid) => {
    if (!window.confirm("Freundschaft wirklich beenden?")) return;
    try {
      await api.delete(`/friends/${uid}`);
      toast.success("Freundschaft beendet");
      loadAll();
    } catch { toast.error("Fehler"); }
  };

  const TABS = [
    { id: "incoming", label: `Eingehend (${incoming.length})`, icon: Mail },
    { id: "outgoing", label: `Gesendet (${outgoing.length})`, icon: Users },
    { id: "friends", label: `Freunde (${friends.length})`, icon: Users },
  ];

  return (
    <div className="space-y-4" data-testid="friends-page">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Verbindungen</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter">
          Freunde 🤝
        </h1>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`friends-tab-${t.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${
                active
                  ? "bg-[#1D5BFF] text-white shadow-[0_0_24px_rgba(0,102,255,0.45)]"
                  : "bg-[#FFFFFF] text-slate-700 border border-slate-200 hover:text-slate-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade…
        </div>
      )}

      {!loading && tab === "incoming" && (
        <List items={incoming} empty="Keine offenen Anfragen.">
          {(r) => (
            <Row
              key={r.id}
              avatar={r.from_user_avatar}
              name={r.from_user_name}
              sub="hat dir eine Freundschaftsanfrage gesendet."
            >
              <button onClick={() => accept(r.id)} data-testid={`accept-${r.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" /> Annehmen
              </button>
              <button onClick={() => reject(r.id)} data-testid={`reject-${r.id}`} className="bg-red-500/80 hover:bg-red-500 text-slate-900 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5">
                <X className="w-3.5 h-3.5" /> Ablehnen
              </button>
            </Row>
          )}
        </List>
      )}

      {!loading && tab === "outgoing" && (
        <List items={outgoing} empty="Du hast keine offenen Anfragen gesendet.">
          {(r) => (
            <Row
              key={r.id}
              avatar={r.to_user_avatar}
              name={r.to_user_name}
              sub="wartet auf Antwort."
            />
          )}
        </List>
      )}

      {!loading && tab === "friends" && (
        <List items={friends} empty="Du hast noch keine Freunde verbunden. Sende eine Anfrage über die Mitglieder-Seite.">
          {(u) => (
            <Row
              key={u.id}
              avatar={u.avatar_url}
              name={u.name}
              sub={u.location || "—"}
            >
              <button
                onClick={() => navigate(`/nachrichten/${u.id}`)}
                data-testid={`message-friend-${u.id}`}
                className="bg-[#1D5BFF] hover:bg-[#1546C9] text-white text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Nachricht
              </button>
              <button
                onClick={() => unfriend(u.id)}
                data-testid={`unfriend-${u.id}`}
                className="bg-slate-100/70 hover:bg-red-500/30 text-red-300 text-xs font-semibold px-3 py-2 rounded-full flex items-center gap-1.5"
              >
                <UserMinus className="w-3.5 h-3.5" /> Entfreunden
              </button>
            </Row>
          )}
        </List>
      )}
    </div>
  );
}

function List({ items, empty, children }) {
  if (items.length === 0) {
    return <div className="bg-[#FFFFFF] border border-slate-200/60 rounded-2xl p-10 text-center text-slate-500 text-sm">{empty}</div>;
  }
  return <div className="space-y-3">{items.map(children)}</div>;
}

function Row({ avatar, name, sub, children }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-4 flex items-center gap-4 flex-wrap">
      <img src={fileUrl(avatar)} alt={name} className="w-12 h-12 rounded-full object-cover border border-slate-200" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900">{name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}
